import { useMemo } from 'react';

import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { createLink } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';

import {
  OBJECTIVE_LABELS,
  SECONDARY_GOAL_LABELS,
} from '../components/mediaPlanLabels';

import type { MediaPlanListItem } from '../types';

import Folder from '@/shared/components/icons/lucide/Folder';
import { Tooltip } from '@/shared/components/tooltip';
import { useGlobalFilterKeys } from '@/shared/hooks';

const Link = createLink(MuiLink);

const columnHelper = createColumnHelper<MediaPlanListItem>();

const useMediaPlanColumns = () => {
  const { isMeta } = useGlobalFilterKeys();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => (
          <Typography variant='body3' component='span'>
            Media Plan Name
          </Typography>
        ),
        cell: ({ getValue, row }) => {
          const name = getValue();
          return (
            <Link
              to='/media-plans/$id'
              params={{ id: row.original.id }}
              underline='hover'
              color='primary'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                maxWidth: '100%',
                minWidth: 0,
              }}
            >
              <Folder
                sx={{
                  color: 'primary.main',
                  fontSize: 20,
                  display: 'block',
                  flexShrink: 0,
                }}
              />
              <Tooltip title={name} placement='top-start' arrow={false}>
                <Typography
                  variant='subtitleNumbers'
                  component='span'
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    minWidth: 0,
                  }}
                >
                  {name}
                </Typography>
              </Tooltip>
            </Link>
          );
        },
        size: 240,
      }),
      columnHelper.accessor('platform', {
        header: () => (
          <Typography variant='body3' component='span'>
            Platform
          </Typography>
        ),
        cell: ({ getValue }) => (
          <Typography
            variant='subtitleNumbers'
            component='span'
            color='text.tertiary'
          >
            {getValue()}
          </Typography>
        ),
        size: 140,
      }),
      columnHelper.accessor(isMeta ? 'account' : 'advertiser', {
        header: () => (
          <Typography variant='body3' component='span'>
            {isMeta ? 'Account' : 'Advertiser'}
          </Typography>
        ),
        cell: ({ getValue }) => (
          <Typography
            variant='subtitleNumbers'
            component='span'
            color='text.tertiary'
          >
            {getValue()}
          </Typography>
        ),
        size: 160,
      }),
      columnHelper.accessor('dataMode', {
        header: () => (
          <Typography variant='body3' component='span'>
            Objective
          </Typography>
        ),
        cell: ({ getValue }) => (
          <Typography
            variant='subtitleNumbers'
            component='span'
            color='text.tertiary'
          >
            {OBJECTIVE_LABELS[getValue()] ?? getValue()}
          </Typography>
        ),
        size: 160,
      }),
      columnHelper.accessor('secondaryGoal', {
        header: () => (
          <Typography variant='body3' component='span'>
            Secondary Goal
          </Typography>
        ),
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.tertiary'
            >
              {value ? (SECONDARY_GOAL_LABELS[value] ?? value) : '—'}
            </Typography>
          );
        },
        size: 140,
      }),
      columnHelper.accessor((row) => row.audienceLibrary?.name ?? null, {
        id: 'audienceLibraryName',
        header: () => (
          <Typography variant='body3' component='span'>
            Audience
          </Typography>
        ),
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.tertiary'
            >
              {value ?? 'No Audience'}
            </Typography>
          );
        },
        size: 180,
      }),
      columnHelper.accessor('associatedCampaignsCount', {
        header: () => (
          <Typography variant='body3' component='span'>
            Campaigns
          </Typography>
        ),
        cell: ({ getValue }) => (
          <Typography
            variant='subtitleNumbers'
            component='span'
            color='text.tertiary'
          >
            {getValue()}
          </Typography>
        ),
        size: 160,
      }),
    ],
    [isMeta]
  );

  return columns;
};

export default useMediaPlanColumns;
